import { useState } from 'react'

import {
  EMPTY_STRING,
  FLOATING_ELEMENT_PLACEMENT,
} from '../../../../../../constants/common'
import { useFloatingSelect } from '../../../../../../hooks'
import { isEmptyString } from '../../../../../../utils/common'
import { stopPropagation } from '../../../../../../utils/events'
import { GretaAIChatFeedback } from '../../../../GretaAIChat.constants'
import {
  getIsGretaAIChatFeedbackDown,
  getIsGretaAIChatFeedbackUp,
} from '../../../../GretaAIChat.utils'

export function useGretaAIChatAssistantFeedback({
  feedback,
  onSubmitFeedback,
}) {
  const [pendingValue, setPendingValue] = useState(null)
  const [reason, setReason] = useState(null)
  const [comment, setComment] = useState(EMPTY_STRING)

  // Dismissal drops only the pending thumb; the reason/comment draft survives.
  function discardPendingFeedback() {
    setPendingValue(null)
  }

  // Cancel / submit clears everything — a later visit starts fresh.
  function resetFeedback() {
    setPendingValue(null)
    setReason(null)
    setComment(EMPTY_STRING)
  }

  const { context, floatingStyles, getFloatingProps, isOpen, refs, setOpen } =
    useFloatingSelect({
      placement: FLOATING_ELEMENT_PLACEMENT.topStart,
      shouldClampHeight: false,
      onClose: discardPendingFeedback,
    })

  function handleFeedback(event, value) {
    stopPropagation(event)

    if (pendingValue === value) {
      discardPendingFeedback()
      setOpen(false)

      return
    }

    if (
      feedback?.value === value &&
      reason === null &&
      isEmptyString(comment)
    ) {
      setReason(feedback.reason ?? null)
      setComment(feedback.comment ?? EMPTY_STRING)
    }

    setPendingValue(value)
    setOpen(true)
  }

  function handleFeedbackUp(event) {
    handleFeedback(event, GretaAIChatFeedback.Up)
  }

  function handleFeedbackDown(event) {
    handleFeedback(event, GretaAIChatFeedback.Down)
  }

  function handleCancelFeedback() {
    resetFeedback()
    setOpen(false)
  }

  function handleSubmitFeedback() {
    const isUpVote = getIsGretaAIChatFeedbackUp(pendingValue)

    onSubmitFeedback({
      comment: comment.trim(),
      reason: isUpVote ? null : reason,
      value: pendingValue,
    })
    resetFeedback()
    setOpen(false)
  }

  const shownValue = pendingValue ?? feedback?.value
  const isUp = getIsGretaAIChatFeedbackUp(shownValue)
  const isDown = getIsGretaAIChatFeedbackDown(shownValue)

  return {
    comment,
    context,
    floatingStyles,
    getFloatingProps,
    handleCancelFeedback,
    handleFeedbackDown,
    handleFeedbackUp,
    handleSubmitFeedback,
    isDown,
    isOpen,
    isUp,
    pendingValue,
    reason,
    refs,
    setComment,
    setReason,
  }
}
